import { toPng } from 'html-to-image'
import type { Options } from 'html-to-image/lib/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'react-toastify'
import type {
  RankingContentTypeValues,
  RankingRangeTypeValues,
} from 'src/components/Ranking/ranking-utils'
import type { Media } from 'src/services/apollo/generated/graphql'
import { getPublicUrl } from 'src/utils/media'
import { fetchToBase64 } from '../ArtworkDetail/utils'
import { RankingOverlay } from './RankingOverlay'

const SNAPSHOT_ELEMENT_ID = 'ranking-snapshot'

export function useRankingSnapshot(
  media: Media,
  rankingOpts: {
    filterType: RankingRangeTypeValues
    type: RankingContentTypeValues
    rankNumber: number
  },
  title: string,
) {
  const [imgBase64, setImgBase64] = useState<string | undefined>(undefined)
  const [isReady, setIsReady] = useState(false)
  const cachedSnapshotRef = useRef<string | undefined>(undefined)

  const snapshot = useCallback(async () => {
    if (cachedSnapshotRef.current) return cachedSnapshotRef.current

    const element = document.getElementById(SNAPSHOT_ELEMENT_ID)
    if (!element) return

    const options = {
      width: element.clientWidth,
      height: element.clientHeight,
      pixelRatio: 1,
    } satisfies Options

    try {
      const imageFile = await toPng(element, options)
      return imageFile
    } catch (e: any) {
      toast.error(`${e?.message}`)
    }
  }, [])

  const preloadSnapshot = useCallback(async () => {
    const element = document.getElementById(SNAPSHOT_ELEMENT_ID)
    if (!element) {
      setIsReady(true)
      return
    }

    try {
      const result = await toPng(element, {
        width: element.clientWidth,
        height: element.clientHeight,
        pixelRatio: 1,
      })
      cachedSnapshotRef.current = result
    } catch {
      // non-fatal: snapshot will fallback to on-demand generation
    }
    setIsReady(true)
  }, [])

  useEffect(() => {
    const imageUrl = getPublicUrl(media)
    if (imageUrl)
      fetchToBase64(imageUrl).then(str => {
        if (str) {
          setImgBase64(str)
        }
      })
  }, [media.id])

  if (!media.height) {
    return { snapshot, isReady: false, Portal: null }
  }

  // Watermark's height should be scaled to about 1/12 of image height
  const watermarkHeight = (media.height ?? 512) / 12
  const dpr = globalThis.devicePixelRatio ?? 1

  // Watermark's rendered height is ~72px at 1x scale in snapshot context
  const snapshotScale = watermarkHeight / 72 / dpr

  const Portal = createPortal(
    <div
      style={{
        position: 'absolute',
        left: -99999,
        top: -99999,
        pointerEvents: 'none',
      }}
    >
      <div id={SNAPSHOT_ELEMENT_ID} className="relative">
        <img
          src={imgBase64}
          className="object-cover"
          crossOrigin="anonymous"
          onLoad={preloadSnapshot}
          style={{
            maxHeight: media.height / dpr,
          }}
        />
        <div
          className="absolute select-none bottom-0 left-0 w-full"
          style={{ fontSize: `calc(16px * ${snapshotScale})` }}
        >
          <RankingOverlay rankingOpts={rankingOpts} title={title} />
        </div>
      </div>
    </div>,
    document.body,
  )

  return { snapshot, isReady, Portal }
}
